import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  IconButton,
  InputBase,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Grid,
  MenuItem,
  Select,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import createInstance from "../../../api/axiosinstance";
import Toast from "../../../utils/toast";
// import COLORS from "../../Themes/Colours";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const aiDishaApi = createInstance("DISHA");

const TP_interviewermgmt = () => {
  const [interviewers, setInterviewers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loadingId, setLoadingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const { COLORS } = useThemeMode();

  const fetchInterviewers = async () => {
    try {
      const response = await aiDishaApi.get("/get-all-interviewer");
      setInterviewers(response.data.active_users || []);
    } catch (error) {
      console.error("Error fetching interviewers:", error);
      Toast.error("Failed to fetch interviewers");
    }
  };

  useEffect(() => {
    fetchInterviewers();
  }, []);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusChange = async (userId, newStatus) => {
    setLoadingId(userId);
    try {
      await aiDishaApi.patch(
        `/update-interviewer-status?user_id=${userId}&status=${newStatus}`
      );
      Toast.success(`Status updated to "${newStatus}"`);
      await fetchInterviewers();
    } catch (error) {
      console.error("Error updating status:", error);
      Toast.error("Failed to update status");
    } finally {
      setLoadingId(null);
    }
  };

  const handleView = (interviewer) => {
    setSelectedInterviewer(interviewer);
    setIsEditMode(false);
    setModalOpen(true);
  };

  const handleEdit = (interviewer) => {
    setSelectedInterviewer({
      ...interviewer,
      address: interviewer.address || {
        H_No: "",
        city: "",
        state: "",
        country: "",
      },
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedInterviewer(null);
  };

  const handleInputChange = (field, value) => {
    setSelectedInterviewer((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddressChange = (field, value) => {
    setSelectedInterviewer((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { user_id, name, email, mobile, Date_of_birth, address } =
        selectedInterviewer;

      await aiDishaApi.put(`/update-interviewer-profile?user_id=${user_id}`, {
        name,
        email,
        mobile,
        Date_of_birth,
        address,
      });

      Toast.success("Profile updated successfully");
      setModalOpen(false);
      await fetchInterviewers();
    } catch (error) {
      console.error("Error updating profile:", error);
      Toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const filteredInterviewers = interviewers.filter((item) =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedInterviewers = filteredInterviewers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const interviewerHeaders = [
    "User ID",
    "Name",
    "Email",
    "Mobile",
    "Date of Birth",
    "Role",
    "Status",
    "Activation Status",
    "Action",
  ];

  return (
    <div
      style={{
        height: "calc(100% - 6%)",
        overflow: "scroll",
        scrollbarWidth: "thin",
        msOverflowStyle: "none",
      }}
    >
      {/* Search Bar */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "auto",
          alignItems: "center",
          borderRadius: "10px",
          padding: "5% 1% 0% 1%",
        }}
      >
        <Box
          gridColumn="span 12"
          sx={{ marginTop: "auto", textAlign: "left", marginBottom: "2%" }}
        >
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "75%",
              borderRadius: 2,
              background: COLORS.primary,
            }}
          >
            <IconButton
              type="button"
              sx={{ p: "8px", color: COLORS.text }}
              aria-label="search"
            >
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1, 
                fontFamily: "Poppins", 
                color: COLORS.text,
                "& input::placeholder": {
                 color: `${COLORS.text} !important`, // 👈 placeholder color
                 opacity: 10, // important (MUI reduces opacity by default)
               },
               }}
              placeholder="Search by Interviewer Name"
              inputProps={{ "aria-label": "search interviewer" }}
              value={searchTerm}
              onChange={handleSearch}
            />
          </Paper>
        </Box>
      </Box>

      {/* Data Table */}
      <Box xs={1} sx={{ width: "100%", paddingLeft: "16px", paddingRight: "16px" }}>
        <Grid container spacing={1} sx={{ justifyContent: "flex-start" }}>
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              sx={{
                marginTop: "25px",
                borderRadius: "10px",
                width:"100%",
                // marginLeft: "-55px",
              }}
            >
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  <TableRow>
                    {interviewerHeaders.map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          color: COLORS.text,
                          borderBottom: 0,
                          fontWeight: "bold",
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ background: COLORS.background }}>
                  {paginatedInterviewers.length > 0 ? (
                    paginatedInterviewers.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.user_id}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.name}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.email}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.mobile}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.Date_of_birth
                            ? new Date(item.Date_of_birth).toLocaleDateString()
                            : "-"}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.role}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {item.status}
                        </TableCell>
                        <TableCell sx={{ color: COLORS.text }}>
                          {loadingId === item.user_id ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            <Select
                              size="small"
                              value={item.activation_status || "Pending"}
                              onChange={(e) =>
                                handleStatusChange(item.user_id, e.target.value)
                              }
                              sx={{
                                backgroundColor: COLORS.background,
                                color: COLORS.text,
                                "& .MuiSelect-icon": { color: COLORS.text },
                              }}
                            >
                              <MenuItem sx={{ color: COLORS.text }} value="Pending">
                                Pending
                              </MenuItem>
                              <MenuItem
                                sx={{ color: COLORS.text }}
                                value="Completed"
                              >
                                Completed
                              </MenuItem>
                            </Select>
                          )}
                        </TableCell>

                        {/* Action Buttons */}
                        <TableCell sx={{ color: COLORS.text }}>
                          <IconButton
                            color="primary"
                            onClick={() => handleView(item)}
                          >
                            <VisibilityIcon sx={{ color: COLORS.text }} />
                          </IconButton>
                          <IconButton
                            color="secondary"
                            onClick={() => handleEdit(item)}
                          >
                            <EditIcon sx={{ color: COLORS.text }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={interviewerHeaders.length}
                        sx={{ textAlign: "center", color: COLORS.text }}
                      >
                        No Data Available
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredInterviewers.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                background: COLORS.primary,
                color: COLORS.text,
                // marginLeft: "-55px",
                width: "100%",
                borderRadius: "0 0 10px 10px",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* View/Edit Modal */}
      {selectedInterviewer && (
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            style: { background: COLORS.primary , borderRadius: "8px" },
          }}
        >
          <div
            // className="modal-content"
            style={{
              background: COLORS.primary ,
              color: COLORS.text,
              padding: "10px",
            }}
          >
            <div className="modal-header border-0 d-flex justify-content-between align-items-center">
              <h5
                className="modal-title"
                style={{
                  color: COLORS.text,
                  fontFamily: "Poppins",
                  fontWeight: "600",
                }}
              >
                {isEditMode
                  ? "Edit Interviewer Profile"
                  : "View Interviewer Profile"}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleModalClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body" style={{ padding: "12px" }}>
              <form>
                <div className="row" style={{ margin: "0 -12px" }}>
                  {/* User ID - Read Only */}
                  <div
                    className="col-sm-3"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: COLORS.text,
                      }}
                    >
                      User ID
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedInterviewer.user_id || ""}
                      readOnly
                      style={{
                        background: COLORS.background,
                        border: "red",
                        color: COLORS.text,
                        
                      }}
                    />
                  </div>

                  {/* Name */}
                  <div
                    className="col-sm-3"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: COLORS.text,
                      }}
                    >
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Enter Name"
                      value={selectedInterviewer.name || ""}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        background: COLORS.background,
                        color: COLORS.text,
                        border: "none",
                      }}
                    />
                  </div>
                
                  

                  {/* Date of Birth */}
                  <div
                    className="col-sm-4"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: COLORS.text,
                      }}
                    >
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={
                        selectedInterviewer.Date_of_birth
                          ? selectedInterviewer.Date_of_birth.split("T")[0]
                          : ""
                      }
                      onChange={(e) =>
                        handleInputChange("Date_of_birth", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        background: COLORS.background,
                        color: COLORS.text,
                        border: "none",
                      }}
                    />
                  </div>
                </div>

                <div className="row" style={{ margin: "0 -12px" }}>
                    {/* Email */}
                  <div
                    className="col-sm-5"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: COLORS.text,
                      }}
                    >
                      Email ID
                    </label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={selectedInterviewer.email || ""}
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        background: COLORS.background,
                        color: COLORS.text,
                        border: "none",
                      }}
                    />
                  </div>
                  {/* Mobile */}
                  <div
                    className="col-sm-5"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: COLORS.text,
                      }}
                    >
                      Mobile
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={selectedInterviewer.mobile || ""}
                      onChange={(e) =>
                        handleInputChange("mobile", e.target.value)
                      }
                      disabled={!isEditMode}
                      style={{
                        background: COLORS.background,
                        color: COLORS.text,
                        border: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Address Fields */}
                <div className="row" style={{ margin: "0 -12px" }}>
                  <div
                    className="col-12"
                    style={{ padding: "0 12px", marginBottom: "1rem" }}
                  >
                    <label
                      style={{
                        fontFamily: "Poppins",
                        fontSize: "12px",
                        color: COLORS.text,
                      }}
                    >
                      Address
                    </label>
                  </div>
                  {["H_No", "city", "state", "country"].map((field) => (
                    <div
                      key={field}
                      className="col-sm-5"
                      style={{ padding: "0 12px", marginBottom: "1rem" }}
                    >
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={field.replace("_", " ")}
                        value={selectedInterviewer.address?.[field] || ""}
                        onChange={(e) =>
                          handleAddressChange(field, e.target.value)
                        }
                        disabled={!isEditMode}
                        style={{
                          background: COLORS.background,
                          color: COLORS.text,
                          border: "none",
                        }}
                      />
                    </div>
                  ))}
                </div>
              </form>
            </div>

            <div className="modal-footer border-0 d-flex justify-content-end gap-3">
              <button
                type="button"
                className="btn btn-sm rounded-2 fw-medium"
                onClick={handleModalClose}
                style={{
                  color: COLORS.text,
                  width: "120px",
                  border: "1px solid #033538",
                }}
              >
                Cancel
              </button>

              {isEditMode && (
                <button
                  type="button"
                  className="btn btn-sm rounded-2 fw-medium"
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    background: COLORS.background,
                    color: COLORS.text,
                    width: "120px",
                  }}
                >
                  {saving ? (
                    <div
                      className="spinner-border spinner-border-sm text-light"
                      role="status"
                    >
                      <span className="visually-hidden">Saving...</span>
                    </div>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              )}
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default TP_interviewermgmt;
